import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TanTable } from '../../components/TanTable';
import { useToast } from '../../contexts/ToastContext';
import { deletedRowClass } from '../../utils/deletedRow';
import DocsFilterBar from '../pro/components/docs/DocsFilterBar';
import DocPreviewLightbox from '../pro/components/docs/DocPreviewLightbox';
import { previewDocumentBlob } from '../../services/documentsSearch.service';
import { MAX_PRINT_SELECTION } from '../../types/documentsSearch';
import type {
  BulkEmailResponse, BulkItem, DocumentSearchFilters, DocumentSearchRow,
} from '../../types/documentsSearch';
import {
  useAdditionalFieldColumns, useAdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import { useDocSelection } from './useDocSelection';
import BulkEmailModal from './BulkEmailModal';
import {
  buildDocumentColumns, DEFAULT_DOCUMENT_FILTERS, DOCUMENT_COLUMN_FILTER_TYPES,
  DOCUMENT_ROW_KEYS, useDocumentBulkOps, useDocumentSearch, useDocumentTypes,
} from './documentsGridKit';

/**
 * Documents view for Search Results — every document attached to the PROs the
 * search returned, filterable and bulk-actionable (email / zip / print).
 *
 * V1 parity target: searchdisplay=documents. Rendered INSTEAD of the orders
 * grid when the page's Documents toggle is on, so it owns its own filter bar
 * and grid but nothing else on the page.
 */
export interface SearchDocumentsViewProps {
  /** Numeric pronumbers from the search result set (templates excluded). */
  pros: number[];
  /** Opens the Pro drawer — the host page's own row-click handler. */
  onProClick?: (pronumber: string) => void;
  /**
   * Pre-fills the bulk-email modal's first recipient with the signed-in user.
   * Threaded from the host page (SearchResults reads it off the MSAL account)
   * rather than read here, so this view stays free of an auth dependency.
   */
  defaultRecipient?: string;
}


/** One key for the grid layout AND the added-column selection, so the two travel
 *  together (`additional_fields_<key>` is the setting the hook writes). */
const DOCUMENTS_SETTINGS_KEY = 'search-results-documents-table';

const ACTION_BTN =
  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-white '
  + 'disabled:cursor-not-allowed disabled:opacity-50';

const SearchDocumentsView: React.FC<SearchDocumentsViewProps> = ({
  pros, onProClick, defaultRecipient = '',
}) => {
  const { showToast } = useToast();
  const [filters, setFilters] = useState<DocumentSearchFilters>(DEFAULT_DOCUMENT_FILTERS);
  const [previewing, setPreviewing] = useState<DocumentSearchRow | null>(null);
  /**
   * The selection SNAPSHOT the email modal is working on (null = closed). Held
   * separately from the live selection so clearing the grid on success doesn't
   * yank the modal's own items out from under it.
   */
  const [emailItems, setEmailItems] = useState<BulkItem[] | null>(null);

  // Order columns the user added. A document row carries the PRO it hangs off, so the
  // whole orders registry resolves against it — "which customer / delivery
  // appointment / terminal is this POD for" without leaving the Documents view. The
  // selection sits up here with the fetch because the server does the attaching; the
  // columns are built further down. See useAdditionalFields.
  const docFields = useAdditionalFieldSelection(DOCUMENTS_SETTINGS_KEY);

  const { rows, loading, error, truncated, reload, prosKey } = useDocumentSearch(
    pros, filters,
    { additionalFields: docFields.additionalFields, ready: docFields.ready },
  );
  const ops = useDocumentBulkOps();
  const { busy } = ops;

  const selection = useDocSelection();
  const { clear, checkAll, toggle, isSelected, count, items } = selection;

  const overCap = ops.overBulkCap(count);
  const overPrintCap = ops.overPrintCap(count);
  const capMessage = ops.capMessage(count);

  const docTypesQuery = useDocumentTypes();

  // A different search = a different document universe; the old selection's
  // recnos are meaningless (and the bulk endpoints would 400 on them).
  useEffect(() => { clear(); setEmailItems(null); }, [prosKey, clear]);

  const handleSent = useCallback((res: BulkEmailResponse) => {
    const to = res.recipients.join(', ');
    showToast({
      message: res.failed.length > 0
        ? `${res.sent} document(s) emailed to ${to}. `
          + `${res.failed.length} could not be sent — see the list.`
        : `${res.sent} document(s) emailed to ${to}.`,
      type: res.failed.length > 0 ? 'warning' : 'success',
    });
    clear();
    // Only auto-close on a clean send: when some documents failed the modal is
    // the only place naming WHICH ones, so leave it up for the user to read.
    if (res.failed.length === 0) setEmailItems(null);
  }, [clear, showToast]);

  /**
   * Blob loader for the lightbox  /**
   * Blob loader for the lightbox, keyed on the row being previewed.
   *
   * The generic /documents/preview route is used instead of the per-PRO one
   * because a result row can come from any of the three document sources and
   * only `cms` rows exist in cms_map. Memoized on doc_key so the lightbox's
   * fetch effect doesn't re-run on every parent render.
   */
  const loadPreviewBlob = useMemo(
    () => (previewing
      ? () => previewDocumentBlob({
        source: previewing.source, ref: previewing.ref,
        fileinfo_recno: previewing.fileinfo_recno,
        pronumber: previewing.pronumber,
      })
      : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previewing?.doc_key],
  );

  const baseColumns = useMemo(
    () => buildDocumentColumns({
      onProClick,
      onPreview: setPreviewing,
      selection: { isSelected, toggle, checkAll },
    }),
    [isSelected, checkAll, toggle, onProClick],
  );
  const { columns, availableFields } = useAdditionalFieldColumns<DocumentSearchRow>(
    docFields, { baseColumns, coveredFields: DOCUMENT_ROW_KEYS },
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 px-3">
      {truncated && (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200"
        >
          Showing the first 10,000 documents — narrow the date range or document types.
        </div>
      )}

      <DocsFilterBar
        value={filters}
        onChange={setFilters}
        docTypes={docTypesQuery.data || []}
      />

      <div className="min-h-0 flex-1">
        <TanTable<DocumentSearchRow>
          data={rows}
          columns={columns}
          loading={loading}
          errorMessage={error}
          onRetry={reload}
          settingsKey={DOCUMENTS_SETTINGS_KEY}
          fieldName={DOCUMENTS_SETTINGS_KEY}
          cardTitle="Documents"
          searchPlaceholder="Filter documents..."
          availableFields={availableFields}
          additionalFields={docFields.additionalFields}
          onAdditionalFieldsChange={docFields.onAdditionalFieldsChange}
          enableColumnFilters
          // Text filters, not dropdowns — see DOCUMENT_COLUMN_FILTER_TYPES.
          columnFilterTypes={DOCUMENT_COLUMN_FILTER_TYPES}
          enableGrouping={false}
          enablePagination={false}
          fillContainer
          getRowId={(row) => row.doc_key}
          getRowClassName={(row, m) => deletedRowClass(row.deleted === 'Y', m.zebra)}
        />
      </div>

      {count > 0 && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white/95 px-3 py-2 backdrop-blur dark:border-gray-700 dark:bg-gray-800/95">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {count.toLocaleString()} document{count === 1 ? '' : 's'} selected
          </span>
          <button
            type="button"
            onClick={() => setEmailItems(items)}
            disabled={overCap || busy !== null}
            title="Email the selected documents"
            className={`${ACTION_BTN} bg-blue-600 hover:bg-blue-700`}
          >
            Email…
          </button>
          <button
            type="button"
            onClick={() => ops.runZip(items)}
            disabled={overCap || busy !== null}
            aria-busy={busy === 'zip'}
            className={`${ACTION_BTN} bg-slate-600 hover:bg-slate-700`}
          >
            {busy === 'zip' ? 'Zipping…' : 'Download ZIP'}
          </button>
          <button
            type="button"
            onClick={() => ops.runPrint(items)}
            disabled={overCap || overPrintCap || busy !== null}
            aria-busy={busy === 'print'}
            title={
              overPrintCap
                ? `Print is limited to ${MAX_PRINT_SELECTION.toLocaleString()} `
                  + 'documents — use Download ZIP for larger selections.'
                : 'Merge the selected documents into one PDF'
            }
            className={`${ACTION_BTN} bg-slate-600 hover:bg-slate-700`}
          >
            {busy === 'print' ? 'Merging…' : 'Print'}
          </button>
          <button
            type="button"
            aria-label="Clear document selection"
            onClick={clear}
            disabled={busy !== null}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Clear
          </button>
          {capMessage && (
            <div
              role="alert"
              className="w-full text-sm font-medium text-amber-700 dark:text-amber-300"
            >
              {capMessage}
            </div>
          )}
        </div>
      )}

      {emailItems && (
        <BulkEmailModal
          items={emailItems}
          defaultRecipient={defaultRecipient}
          onClose={() => setEmailItems(null)}
          onSent={handleSent}
        />
      )}

      {previewing && (
        <DocPreviewLightbox
          pronumber={previewing.pronumber}
          loadBlob={loadPreviewBlob}
          filename={previewing.filename}
          filetype={previewing.filetype}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
};

export default SearchDocumentsView;
